import * as i0 from '@angular/core';
import { Injectable, Inject, Component, NgModule } from '@angular/core';
import * as i5 from '@angular/common/http';
import { HttpHeaders, HttpRequest, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Subject, from, of, switchMap, catchError } from 'rxjs';
import { takeUntil, first, map } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import * as i2 from '@wdpr/ra-angular-logger';
import * as i3 from '@wdpr/ra-angular-native-bridge';
import { NativeBridgeModule, NativeBridgeProvider } from '@wdpr/ra-angular-native-bridge';
import * as i1 from 'ngx-cookie-service';

class RaInjectOneidAuthTokenConfig {
}

const GUEST_TOKEN_URL = '/profile-api/authentication/get-client-token';
const AUTH_INTERCEPTOR_SKIP_HEADER = 'skip-intercept';

const HybridSsoPlugin = {
    GET_TOKEN_DATA: 'SSOPlugin.getTokenData',
    SEND_TOKEN_DATA: 'disney.SSOPlugin.sendTokenData'
};

class WindowService {
    constructor(document) {
        this.document = document;
        this.window = this.document.defaultView;
    }
    getWindowObject() {
        return this.window;
    }
}
WindowService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: WindowService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
WindowService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: WindowService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: WindowService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

class CookieHandlerService {
    constructor(_cookieService) {
        this._cookieService = _cookieService;
        this.fallbackCookieName = 'pep_jwt_token';
    }
    getCookie(cookieName) {
        return this._cookieService.get(cookieName);
    }
    getFallbackCookie() {
        return this.getCookie(this.fallbackCookieName);
    }
}
CookieHandlerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: CookieHandlerService, deps: [{ token: i1.CookieService }], target: i0.ɵɵFactoryTarget.Injectable });
CookieHandlerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: CookieHandlerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: CookieHandlerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.CookieService }]; } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class RaInjectOneidAuthTokenService {
    constructor(windowService, loggerService, nativeBridgeService, cookieHandlerService, httpService) {
        this.windowService = windowService;
        this.loggerService = loggerService;
        this.nativeBridgeService = nativeBridgeService;
        this.cookieHandlerService = cookieHandlerService;
        this.httpService = httpService;
        this.config = {
            ENABLED: true,
            NONLOGGEDINFLOW: false,
            LOGGEDINFLOW: true,
            RAHYBRID_IOS_MIN_SUPPORTED_MAJOR: 12,
            RAHYBRID_IOS_MIN_SUPPORTED_MINOR: 4,
            RAHYBRID_IOS_MIN_SUPPORTED_PATCH: 0,
            RAHYBRID_AND_MIN_SUPPORTED_MAJOR: 8,
            RAHYBRID_AND_MIN_SUPPORTED_MINOR: 6,
            RAHYBRID_AND_MIN_SUPPORTED_PATCH: 0
        };
        this.destroy$ = new Subject();
        this.isFetchingAccessToken = false;
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    async init(config) {
        this.config = { ...this.config, ...config };
        // Log when the service is initialized
        this.windowObject = this.windowService.getWindowObject().window;
        // Check if the app is running in a hybrid environment
        if (!this.isHybrid()) {
            // Retrieve the window object and PEP (Profile and Entitlement Platform) profile authentication
            const pepProfileAuth = this.windowObject['PEP']?.PROFILE_AUTH;
            // Check if PEP object is not initialized
            if (!pepProfileAuth) {
                this.loggerService.warn('RA Lib Service: PEP object not initialized');
            }
            else {
                // Set the PEP global object instance
                this.pepGlobalObjectInstance = pepProfileAuth;
                // Set event handlers for login and logout events
                this.setOneIDEventHandlers();
                // Get the logged-in status and guest data
                await this.refresh();
            }
        }
    }
    // Set event handlers for OneID login and logout events
    setOneIDEventHandlers() {
        this.pepGlobalObjectInstance.on('login', async () => {
            await this.refresh();
        });
        this.pepGlobalObjectInstance.on('logout', async () => {
            // Clear data on logout
            this.clear();
        });
    }
    // Get the logged-in status from the OneID platform
    refreshLoggedInStatus() {
        try {
            return from(this.pepGlobalObjectInstance?.getLoggedInStatus());
        }
        catch (err) {
            this.loggerService.warn(err);
            return of(false);
        }
    }
    getFallBackToken() {
        // set header to avoid intercept logic infinte loop
        const httpOptions = {
            headers: new HttpHeaders({
                'skip-intercept': 'true',
            })
        };
        return this.httpService.get(GUEST_TOKEN_URL, httpOptions);
    }
    // Extract and observe the guest token
    returnLoginStatus() {
        return this.loggedInStatus;
    }
    // Extract and observe the guest token
    extractGuestToken() {
        return this.token;
    }
    getGuest() {
        return this.guestData;
    }
    // Check if the OneID object is initialized
    isOneIDObjectInitialized() {
        return this.pepGlobalObjectInstance?.isInitialized;
    }
    // Check if the app is running in a hybrid environment
    isHybrid() {
        return Boolean(window.disney.device.embedded);
    }
    // Clear all data
    clear() {
        this.guestData = undefined;
        this.token = undefined;
        this.loggedInStatus = false;
    }
    getConfig() {
        return this.config;
    }
    async refresh() {
        this.loggedInStatus = await this.pepGlobalObjectInstance?.getLoggedInStatus();
        if (this.loggedInStatus) {
            this.guestData = await this.pepGlobalObjectInstance?.getGuest();
            this.token = this.guestData.token.access_token;
        }
    }
    async getOneIdData() {
        return this.isOneIdJwtMessagingSupported()
            ? this.getTokenDataFromNative()
            : this.getTokenDataFromCookie();
    }
    isOneIdJwtMessagingSupported() {
        if (!this.config.ENABLED) {
            return false;
        }
        const RAHYBRID_IOS_UA_REGEX = /RAHybrid\/(?<major>\d+)\.(?<minor>\d+)\.(?<patch>\d+)/;
        const IOS_MIN_MAJOR = this.config.RAHYBRID_IOS_MIN_SUPPORTED_MAJOR;
        const IOS_MIN_MINOR = this.config.RAHYBRID_IOS_MIN_SUPPORTED_MINOR;
        const IOS_MIN_PATCH = this.config.RAHYBRID_IOS_MIN_SUPPORTED_PATCH;
        const RAHYBRID_AND_UA_REGEX = /com.wdpr.ee.ra.rahybrid\/(?<major>\d+)\.(?<minor>\d+)\.(?<patch>\d+)/;
        const AND_MIN_MAJOR = this.config.RAHYBRID_AND_MIN_SUPPORTED_MAJOR;
        const AND_MIN_MINOR = this.config.RAHYBRID_AND_MIN_SUPPORTED_MINOR;
        const AND_MIN_PATCH = this.config.RAHYBRID_AND_MIN_SUPPORTED_PATCH;
        const disney = window.disney;
        const device = disney?.device;
        const uaString = disney.getUserAgent();
        if (device.platform.ios) {
            const { major, minor, patch } = RAHYBRID_IOS_UA_REGEX.exec(uaString).groups;
            return Number(`${major}${minor}${patch}`) >= Number(`${IOS_MIN_MAJOR}${IOS_MIN_MINOR}${IOS_MIN_PATCH}`);
        }
        else if (device.platform.android) {
            const { major, minor, patch } = RAHYBRID_AND_UA_REGEX.exec(uaString).groups;
            return Number(`${major}${minor}${patch}`) >= Number(`${AND_MIN_MAJOR}${AND_MIN_MINOR}${AND_MIN_PATCH}`);
        }
        else {
            return false;
        }
    }
    decodeJwt(jwt) {
        const base64Url = jwt.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join(''));
        return JSON.parse(jsonPayload);
    }
    triggerNativeMessage() {
        // Use @wdpr/ra-angular-native-bridge's "observeFunction" to register
        // a handler for "SSOPlugin.sendTokenData" calls from native RAHybrid
        const nativeMessageObservable$ = this.nativeBridgeService
            .observeFunction(HybridSsoPlugin.SEND_TOKEN_DATA)
            .pipe(takeUntil(this.destroy$), first(), map((data) => {
            const tokenResult = this.parseTokenDataFromNative(data[0], data[1]);
            this.isFetchingAccessToken = false;
            return {
                token: tokenResult.token,
                swid: tokenResult.swid,
                guestData: tokenResult.guestData
            };
        }));
        this.nativeBridgeService.sendCustomMessage(HybridSsoPlugin.GET_TOKEN_DATA, {}, undefined);
        return nativeMessageObservable$;
    }
    getTokenDataFromCookie() {
        const result = {
            token: undefined,
            swid: undefined,
            guestData: undefined
        };
        const pepJwtToken = this.cookieHandlerService.getFallbackCookie();
        try {
            // Parse pep_jwt_token cookie value and try extracting access token
            const decodedJwt = this.decodeJwt(pepJwtToken);
            if (this.isPepJwt(decodedJwt)) {
                const [accessToken, swid] = this.getAccessTokenAndSwidFromPepJwt(decodedJwt);
                result.token = accessToken;
                result.swid = swid;
            }
            else {
                this.loggerService.warn('pep_jwt_token cookie has invalid token');
            }
        }
        catch (error) {
            this.loggerService.warn(`Error parsing token from pep_jwt_token cookie: ${error.message ?? 'Unknown error'}`);
        }
        return Promise.resolve(result);
    }
    getTokenDataFromNative() {
        return new Promise((resolve) => {
            if (!this.isFetchingAccessToken) {
                this.accessTokenSubject$ = new Subject();
                this.isFetchingAccessToken = true;
                this.accessTokenSubject$
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                    next: (tokenResult) => {
                        resolve(tokenResult);
                    },
                    error: (error) => {
                        this.loggerService.warn('Error receiving token from native', error);
                        resolve({
                            token: undefined,
                            swid: undefined,
                            guestData: undefined
                        });
                    }
                });
                // Register handler for processing "SSOPlugin.sendTokenData" message
                // from native that sends OneID access token JWT data.
                this.triggerNativeMessage()
                    // Forward OneID access token data to rxjs Subject so all
                    // registered calls receive the token.
                    .subscribe(this.accessTokenSubject$);
            }
            else {
                this.accessTokenSubject$
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                    next: (tokenResult) => {
                        resolve(tokenResult);
                    },
                    error: (error) => {
                        this.loggerService.warn('Error receiving token from native', error);
                        resolve({
                            token: undefined,
                            swid: undefined,
                            guestData: undefined
                        });
                    }
                });
            }
        });
    }
    parseTokenDataFromNative(encodedJwt, stringifiedGuestData) {
        try {
            // Parse the token received and try extracting access token
            const decodedJwt = this.decodeJwt(encodedJwt);
            // Since native clients may be sending PEP JWT from Profile service
            // until OneID JWT is toggled on, extract access token depending on
            // whether it's PEP JWT or OneID JWT.
            if (this.isPepJwt(decodedJwt)) {
                const [accessToken, swid] = this.getAccessTokenAndSwidFromPepJwt(decodedJwt);
                return {
                    token: accessToken,
                    decodedJwt,
                    swid,
                    guestData: this.getParsedGuestData(stringifiedGuestData),
                };
            }
            // Assuming encodedJwt to be the access token from OneID JWT which contains the swid in the "sub" claim
            return {
                token: encodedJwt,
                decodedJwt,
                swid: decodedJwt.sub,
                guestData: this.getParsedGuestData(stringifiedGuestData),
            };
        }
        catch (error) {
            // Assuming this to be OneID opaque token which requires "swid" to be passed as part of the "stringifiedGuestData" parameter
            const guestData = this.getParsedGuestData(stringifiedGuestData);
            return {
                token: encodedJwt,
                decodedJwt: undefined,
                swid: guestData?.profile.swid,
                guestData
            };
        }
    }
    isPepJwt(decodedJwt) {
        return ('disney' in decodedJwt &&
            'token' in decodedJwt.disney &&
            'access_token' in decodedJwt.disney.token &&
            'swid' in decodedJwt.disney.token);
    }
    getAccessTokenAndSwidFromPepJwt(decodedJwt) {
        return [
            decodedJwt.disney.token.access_token,
            decodedJwt.disney.token.swid,
        ];
    }
    getParsedGuestData(stringifiedGuestData) {
        try {
            return stringifiedGuestData
                ? JSON.parse(stringifiedGuestData)
                : undefined;
        }
        catch (error) {
            this.loggerService.warn(`Error parsing token from native: ${error.message ?? 'Unknown error'}`);
            return undefined;
        }
    }
}
RaInjectOneidAuthTokenService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: RaInjectOneidAuthTokenService, deps: [{ token: WindowService }, { token: i2.LoggerService }, { token: i3.NativeBridgeService }, { token: CookieHandlerService }, { token: i5.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
RaInjectOneidAuthTokenService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: RaInjectOneidAuthTokenService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: RaInjectOneidAuthTokenService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: WindowService }, { type: i2.LoggerService }, { type: i3.NativeBridgeService }, { type: CookieHandlerService }, { type: i5.HttpClient }]; } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class RaInjectOneidAuthTokenInterceptor {
    constructor(raTokenService) {
        this.raTokenService = raTokenService;
        // load the configuration
    }
    /**
     * Intercepts an HTTP request to attach authorization header
     *
     * @param {HttpRequest<any>} request The intercepted HTTP request
     * @param {HttpHandler} next Action to execute after request
     *
     * @returns {Observable<HttpEvent<any>>} The request after intercepted
     */
    intercept(httpRequest, next) {
        this.enabled = this.raTokenService.getConfig().ENABLED;
        this.isNonLoggedInFlowEnabled = this.raTokenService.getConfig().NONLOGGEDINFLOW;
        this.isLoggedInFlowEnabled = this.raTokenService.getConfig().LOGGEDINFLOW;
        if (!this.enabled) {
            return next.handle(httpRequest);
        }
        // Check if OneID object is not initialized and not in a hybrid environment
        if (!this.raTokenService.isOneIDObjectInitialized() && !(this.raTokenService.isHybrid())) {
            return next.handle(httpRequest);
        }
        // skip profile-api intercept
        if (httpRequest.headers.get(AUTH_INTERCEPTOR_SKIP_HEADER)) {
            return next.handle(httpRequest);
        }
        // Check if the request is an instance of HttpRequest
        if (httpRequest instanceof HttpRequest) {
            // If not in a hybrid environment, get token from PEP (Profile and Entitlement Platform) object
            if (!this.raTokenService.isHybrid()) {
                return from(this.initialize()).pipe(switchMap(() => {
                    const loggedInStatus = this.raTokenService.loggedInStatus;
                    if (!loggedInStatus && this.isNonLoggedInFlowEnabled) {
                        return from(this.raTokenService.getFallBackToken()).pipe(switchMap(response => {
                            const token = response['access_token'];
                            return next.handle(httpRequest.clone({
                                setHeaders: {
                                    'Authorization': `Bearer ${token}`,
                                }
                            }));
                        }));
                    }
                    else if (loggedInStatus && this.isLoggedInFlowEnabled) {
                        const token = this.raTokenService.extractGuestToken();
                        return next.handle(httpRequest.clone({
                            setHeaders: {
                                'Authorization': `Bearer ${token}`,
                            }
                        }));
                    }
                    else {
                        return next.handle(httpRequest);
                    }
                }), catchError(err => {
                    console.error('Error in RaInjectOneidAuthTokenInterceptor', JSON.stringify(err));
                    throw err;
                }));
            }
            else {
                // In a hybrid environment, get token data from native bridge service
                return from(this.raTokenService.getOneIdData()).pipe(switchMap(token => {
                    if (token?.token) {
                        return next.handle(httpRequest.clone({
                            setHeaders: {
                                'Authorization': `Bearer ${token.token}`,
                            }
                        }));
                    }
                    return next.handle(httpRequest);
                }));
            }
        }
    }
    // Attach the token to the request headers
    async initialize() {
        // Initialize the RaInjectOneidAuthTokenService
        return await this.raTokenService.init();
    }
}
RaInjectOneidAuthTokenInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: RaInjectOneidAuthTokenInterceptor, deps: [{ token: RaInjectOneidAuthTokenService }], target: i0.ɵɵFactoryTarget.Injectable });
RaInjectOneidAuthTokenInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: RaInjectOneidAuthTokenInterceptor, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: RaInjectOneidAuthTokenInterceptor, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: RaInjectOneidAuthTokenService }]; } });

class RaInjectOneidAuthTokenComponent {
}
RaInjectOneidAuthTokenComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: RaInjectOneidAuthTokenComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
RaInjectOneidAuthTokenComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.5", type: RaInjectOneidAuthTokenComponent, selector: "lib-ra-inject-oneid-auth-token", ngImport: i0, template: ` <p>ra-inject-oneid-auth-token works!</p> `, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: RaInjectOneidAuthTokenComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-ra-inject-oneid-auth-token', template: ` <p>ra-inject-oneid-auth-token works!</p> ` }]
        }] });

class RaInjectOneidAuthTokenModule {
}
RaInjectOneidAuthTokenModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: RaInjectOneidAuthTokenModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RaInjectOneidAuthTokenModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.5", ngImport: i0, type: RaInjectOneidAuthTokenModule, declarations: [RaInjectOneidAuthTokenComponent], imports: [NativeBridgeModule], exports: [RaInjectOneidAuthTokenComponent] });
RaInjectOneidAuthTokenModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: RaInjectOneidAuthTokenModule, providers: [NativeBridgeProvider,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RaInjectOneidAuthTokenInterceptor,
            multi: true
        }
    ], imports: [NativeBridgeModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: RaInjectOneidAuthTokenModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [RaInjectOneidAuthTokenComponent],
                    imports: [NativeBridgeModule],
                    exports: [RaInjectOneidAuthTokenComponent],
                    providers: [NativeBridgeProvider,
                        {
                            provide: HTTP_INTERCEPTORS,
                            useClass: RaInjectOneidAuthTokenInterceptor,
                            multi: true
                        }
                    ]
                }]
        }] });

/*
 * Public API Surface of ra-inject-oneid-auth-token
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AUTH_INTERCEPTOR_SKIP_HEADER, RaInjectOneidAuthTokenComponent, RaInjectOneidAuthTokenConfig, RaInjectOneidAuthTokenInterceptor, RaInjectOneidAuthTokenModule, RaInjectOneidAuthTokenService };
